/**
 * CRM lead timeline — icons and avatars
 */

import React, { useMemo, useState } from 'react'
import { format } from 'date-fns'
import { parseDbDateTime } from '../../../utils/datetime'
import {
  formatTimelineDescription,
  formatTimelineTitle,
} from '../../../lib/crm-display.js'
import { getTimelineActor, getTimelineIconId } from '../../../lib/crm-timeline-visual.js'
import { CrmTimelineIcon } from './CrmTimelineIcons.jsx'

const TIMELINE_ICON_LINE =
  'relative last:after:hidden after:absolute after:top-7 after:bottom-0 after:left-3.5 after:-translate-x-[0.5px] after:border-s after:border-gray-200'

const VISIBLE_EVENT_COUNT = 8

const RAIL_AVATAR = {
  staff: 'bg-green-100 border-green-200 text-green-800',
  client: 'bg-blue-100 border-blue-200 text-blue-800',
}

const RAIL_SYSTEM =
  'flex shrink-0 justify-center items-center size-7 bg-white border border-gray-200 text-gray-600 rounded-full'

function groupEventsByDate(events) {
  const items = []
  let lastDateKey = null

  for (const event of events) {
    const parsed = parseDbDateTime(event.created_at)
    const dateKey = parsed ? format(parsed, 'yyyy-MM-dd') : 'unknown'
    const dateLabel = parsed ? format(parsed, 'MMM d, yyyy') : 'Unknown date'

    if (dateKey !== lastDateKey) {
      items.push({ kind: 'heading', key: `h-${dateKey}`, dateLabel })
      lastDateKey = dateKey
    }
    items.push({ kind: 'event', key: `e-${event.id}`, event })
  }

  return items
}

function TimelineRail({ event, leadDisplayName }) {
  const actor = getTimelineActor(event, { leadDisplayName })
  const iconId = getTimelineIconId(event)

  if (actor.kind === 'staff' || actor.kind === 'client') {
    const tone = RAIL_AVATAR[actor.kind]
    return (
      <span
        className={`flex shrink-0 justify-center items-center size-7 border text-[10px] font-semibold uppercase rounded-full ${tone}`}
        aria-hidden
        title={actor.label}
      >
        {actor.initial}
      </span>
    )
  }

  return (
    <span className={RAIL_SYSTEM} aria-hidden title={actor.label}>
      <CrmTimelineIcon iconId={iconId} className="shrink-0 size-3.5" />
    </span>
  )
}

function TimelineAuthorChip({ actor, timeLabel }) {
  const isClient = actor.kind === 'client'
  const chipAvatar =
    actor.kind === 'system'
      ? null
      : (
          <span
            className={`inline-flex shrink-0 justify-center items-center size-4 border text-[9px] font-medium uppercase rounded-full ${
              isClient
                ? 'bg-blue-50 border-blue-200 text-blue-800'
                : 'bg-gray-100 border-gray-200 text-gray-700'
            }`}
            aria-hidden
          >
            {actor.initial}
          </span>
        )

  return (
    <p className="mt-1.5 inline-flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
      {chipAvatar}
      <span>{actor.label}</span>
      <span className="text-gray-400" aria-hidden>
        ·
      </span>
      {timeLabel.iso ? (
        <time dateTime={timeLabel.iso}>{timeLabel.text}</time>
      ) : (
        <span>{timeLabel.text}</span>
      )}
    </p>
  )
}

function TimelineEventRow({ event, showConnector, leadDisplayName }) {
  const title = formatTimelineTitle(event)
  const description = formatTimelineDescription(event)
  const parsed = parseDbDateTime(event.created_at)
  const timeLabel = {
    text: parsed ? format(parsed, 'h:mm a') : '—',
    iso: parsed?.toISOString(),
  }
  const actor = getTimelineActor(event, { leadDisplayName })
  const iconId = getTimelineIconId(event)
  const iconLineClass = showConnector ? TIMELINE_ICON_LINE : 'relative'

  return (
    <div className="flex gap-x-3">
      <div className={iconLineClass}>
        <div className="relative z-10 size-7 flex justify-center items-center">
          <TimelineRail event={event} leadDisplayName={leadDisplayName} />
        </div>
      </div>

      <div className="grow pt-0.5 pb-8 min-w-0">
        <h3 className="flex gap-x-1.5 font-medium text-gray-900 text-sm">
          <CrmTimelineIcon iconId={iconId} className="shrink-0 size-4 mt-0.5 text-gray-500" />
          <span>{title}</span>
        </h3>
        {description && (
          <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">{description}</p>
        )}
        <TimelineAuthorChip actor={actor} timeLabel={timeLabel} />
      </div>
    </div>
  )
}

function TimelineSection({ items, lastEventId, leadDisplayName }) {
  if (items.length === 0) return null

  return items.map((item) => {
    if (item.kind === 'heading') {
      return (
        <div key={item.key} className="ps-2 my-2 first:mt-0">
          <h3 className="text-xs font-medium uppercase text-gray-500">{item.dateLabel}</h3>
        </div>
      )
    }

    return (
      <TimelineEventRow
        key={item.key}
        event={item.event}
        showConnector={item.event.id !== lastEventId}
        leadDisplayName={leadDisplayName}
      />
    )
  })
}

export default function CrmLeadTimeline({ events = [], leadDisplayName = null }) {
  const [showOlder, setShowOlder] = useState(false)

  const sortedEvents = useMemo(
    () =>
      [...events].sort((a, b) => {
        const ta = parseDbDateTime(a.created_at)?.getTime() ?? 0
        const tb = parseDbDateTime(b.created_at)?.getTime() ?? 0
        return tb - ta
      }),
    [events]
  )

  const hasOlder = sortedEvents.length > VISIBLE_EVENT_COUNT
  const recentEvents = hasOlder ? sortedEvents.slice(0, VISIBLE_EVENT_COUNT) : sortedEvents
  const olderEvents = hasOlder ? sortedEvents.slice(VISIBLE_EVENT_COUNT) : []

  const lastEventId = sortedEvents[sortedEvents.length - 1]?.id

  const recentItems = useMemo(() => groupEventsByDate(recentEvents), [recentEvents])
  const olderItems = useMemo(() => groupEventsByDate(olderEvents), [olderEvents])

  if (sortedEvents.length === 0) {
    return <p className="text-sm text-gray-500">No timeline events yet.</p>
  }

  return (
    <div className="w-full max-h-[520px] overflow-y-auto pe-1">
      <TimelineSection
        items={recentItems}
        lastEventId={hasOlder && !showOlder ? null : lastEventId}
        leadDisplayName={leadDisplayName}
      />

      {hasOlder && showOlder ? (
        <TimelineSection
          items={olderItems}
          lastEventId={lastEventId}
          leadDisplayName={leadDisplayName}
        />
      ) : null}

      {hasOlder && !showOlder ? (
        <div className="ps-2 -ms-px flex gap-x-3">
          <button
            type="button"
            className="inline-flex items-center gap-x-1 text-start text-sm font-medium text-green-700 hover:underline focus:underline focus:outline-none"
            aria-expanded={false}
            onClick={() => setShowOlder(true)}
          >
            <svg
              className="size-3.5 shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
            Show older ({olderEvents.length})
          </button>
        </div>
      ) : null}
    </div>
  )
}
